import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiShield, FiLock, FiUser, FiEye, FiEyeOff,
  FiArrowRight, FiCheck, FiAlertCircle, FiKey
} from 'react-icons/fi';
import { authService } from '../../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

// ─── PIN Input Dot Component ─────────────────────────────────────────────────
const PinDot = ({ filled }) => (
  <motion.div
    animate={{ scale: filled ? 1.2 : 1 }}
    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
    className={`w-4 h-4 rounded-full border-2 transition-all duration-200 ${
      filled
        ? 'bg-slate-900 border-slate-900'
        : 'bg-transparent border-slate-300'
    }`}
  />
);

// ─── Keypad Button ───────────────────────────────────────────────────────────
const KeypadBtn = ({ label, onClick, className = '' }) => (
  <motion.button
    type="button"
    whileTap={{ scale: 0.92 }}
    onClick={onClick}
    className={`w-full py-4 rounded-2xl text-base font-black tracking-wider transition-all select-none
      bg-slate-50 hover:bg-slate-100 active:bg-slate-200 text-slate-900 border border-slate-100
      ${className}`}
  >
    {label}
  </motion.button>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const Register = () => {
  const navigate = useNavigate();

  // Step 1 = PIN entry, Step 2 = Registration form
  const [step, setStep] = useState(1);
  const [pin, setPin]   = useState('');
  const [pinError, setPinError] = useState('');
  const [pinLoading, setPinLoading] = useState(false);

  const [form, setForm] = useState({ username: '', password: '', confirm: '' });
  const [showPass, setShowPass]      = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [regLoading, setRegLoading]  = useState(false);

  // ── PIN keypad actions ──────────────────────────────────────────────────────
  const handleDigit = (d) => {
    if (pin.length < 4) {
      setPinError('');
      setPin(prev => prev + d);
    }
  };

  const handleDelete = () => setPin(prev => prev.slice(0, -1));

  const handlePinSubmit = async (currentPin) => {
    const pinToCheck = currentPin ?? pin;
    if (pinToCheck.length < 4) {
      setPinError('Enter the 4-digit system PIN');
      return;
    }
    try {
      setPinLoading(true);
      setPinError('');
      await authService.verifyPin(pinToCheck);
      // PIN valid — advance to registration form
      setStep(2);
    } catch {
      setPinError('Incorrect PIN. Access denied.');
      setPin('');
    } finally {
      setPinLoading(false);
    }
  };

  // Auto-submit when 4 digits entered
  React.useEffect(() => {
    if (pin.length === 4) handlePinSubmit(pin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  // Physical keyboard support for PIN step
  React.useEffect(() => {
    if (step !== 1) return;
    const onKeyDown = (e) => {
      // Digit keys: number row (0-9) and numpad (Numpad0-Numpad9)
      if (/^[0-9]$/.test(e.key) || (e.code && e.code.startsWith('Numpad') && /^[0-9]$/.test(e.key))) {
        handleDigit(e.key);
      } else if (e.key === 'Backspace') {
        handleDelete();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, pin]);

  // ── Registration submit ─────────────────────────────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      setRegLoading(true);
      await authService.register({
        pin,           // re-sent for server-side double-check
        username: form.username,
        password: form.password
      });
      toast.success('Admin account created! You can now log in.');
      setTimeout(() => navigate('/admin/login'), 1500);
    } catch (err) {
      toast.error(err.message || 'Registration failed');
    } finally {
      setRegLoading(false);
    }
  };

  // ── Shared background ───────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center p-6 relative overflow-hidden font-poppins selection:bg-syana-blue-100 selection:text-syana-blue-900">
      {/* Background blobs */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-400/10 rounded-full blur-[120px] animate-pulse" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[30%] h-[30%] bg-syana-blue-400/10 rounded-full blur-[100px] animate-pulse delay-1000" />

      <AnimatePresence mode="wait">

        {/* ── STEP 1: PIN Gate ─────────────────────────────────────────────── */}
        {step === 1 && (
          <motion.div
            key="pin-gate"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            className="relative z-10 w-full max-w-sm"
          >
            {/* Header */}
            <div className="text-center mb-10">
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                className="w-20 h-20 bg-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-slate-200"
              >
                <FiKey className="text-white text-3xl" />
              </motion.div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tighter uppercase">System Access PIN</h1>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mt-2">
                Enter the 4-digit authorisation code
              </p>
            </div>

            {/* Card */}
            <div className="bg-white/80 backdrop-blur-2xl p-8 rounded-2xl border border-white shadow-2xl shadow-slate-200/50">

              {/* PIN dots */}
              <div className="flex justify-center space-x-5 mb-8">
                {[0, 1, 2, 3].map(i => (
                  <PinDot key={i} filled={i < pin.length} />
                ))}
              </div>

              {/* Error */}
              <AnimatePresence>
                {pinError && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center space-x-2 bg-red-50 border border-red-100 text-red-600 rounded-xl px-4 py-3 mb-6 text-[11px] font-bold"
                  >
                    <FiAlertCircle className="shrink-0" />
                    <span>{pinError}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Keypad */}
              <div className="grid grid-cols-3 gap-3">
                {['1','2','3','4','5','6','7','8','9'].map(d => (
                  <KeypadBtn key={d} label={d} onClick={() => handleDigit(d)} />
                ))}
                {/* Bottom row */}
                <div /> {/* empty slot */}
                <KeypadBtn label="0" onClick={() => handleDigit('0')} />
                <KeypadBtn
                  label="⌫"
                  onClick={handleDelete}
                  className="text-slate-400 hover:text-slate-900"
                />
              </div>

              {/* Loading state */}
              <AnimatePresence>
                {pinLoading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center justify-center mt-6 space-x-2 text-slate-400"
                  >
                    <div className="w-4 h-4 border-2 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
                    <span className="text-[10px] font-black uppercase tracking-widest">Verifying...</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <p className="text-center mt-8 text-[9px] font-black text-slate-300 uppercase tracking-widest">
              Siyane Optometrists · Restricted Access
            </p>
          </motion.div>
        )}

        {/* ── STEP 2: Registration Form ────────────────────────────────────── */}
        {step === 2 && (
          <motion.div
            key="reg-form"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            className="relative z-10 w-full max-w-md"
          >
            {/* Header */}
            <div className="text-center mb-10">
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                className="w-20 h-20 bg-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-slate-200 relative"
              >
                <FiShield className="text-white text-3xl" />
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center shadow-md">
                  <FiCheck className="text-white text-xs" />
                </div>
              </motion.div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tighter uppercase">Create Admin Account</h1>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mt-2">
                PIN verified · Set your credentials
              </p>
            </div>

            {/* Card */}
            <div className="bg-white/80 backdrop-blur-2xl p-10 rounded-2xl border border-white shadow-2xl shadow-slate-200/50">
              <form onSubmit={handleRegister} className="space-y-6">

                {/* Username */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-2">
                    Username
                  </label>
                  <div className="relative group">
                    <FiUser className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-slate-900 transition-colors" />
                    <input
                      type="text"
                      placeholder="Choose a username"
                      required
                      autoComplete="off"
                      value={form.username}
                      onChange={e => setForm({ ...form, username: e.target.value })}
                      className="w-full pl-14 pr-6 py-4 bg-slate-50 border-2 border-slate-50 rounded-2xl focus:border-slate-900 focus:bg-white outline-none transition-all text-sm font-bold"
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-2">
                    Password
                  </label>
                  <div className="relative group">
                    <FiLock className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-slate-900 transition-colors" />
                    <input
                      type={showPass ? 'text' : 'password'}
                      placeholder="Min. 6 characters"
                      required
                      value={form.password}
                      onChange={e => setForm({ ...form, password: e.target.value })}
                      className="w-full pl-14 pr-14 py-4 bg-slate-50 border-2 border-slate-50 rounded-2xl focus:border-slate-900 focus:bg-white outline-none transition-all text-sm font-bold"
                    />
                    <button type="button" onClick={() => setShowPass(!showPass)}
                      className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-900 transition-colors">
                      {showPass ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-2">
                    Confirm Password
                  </label>
                  <div className="relative group">
                    <FiLock className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-slate-900 transition-colors" />
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      placeholder="Re-enter password"
                      required
                      value={form.confirm}
                      onChange={e => setForm({ ...form, confirm: e.target.value })}
                      className="w-full pl-14 pr-14 py-4 bg-slate-50 border-2 border-slate-50 rounded-2xl focus:border-slate-900 focus:bg-white outline-none transition-all text-sm font-bold"
                    />
                    <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-900 transition-colors">
                      {showConfirm ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Strength indicator */}
                {form.password && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-1 px-2">
                    <div className="flex space-x-1">
                      {[...Array(4)].map((_, i) => (
                        <div key={i} className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                          form.password.length > i * 3
                            ? form.password.length >= 12 ? 'bg-emerald-500'
                              : form.password.length >= 8 ? 'bg-indigo-400'
                              : 'bg-amber-400'
                            : 'bg-slate-100'
                        }`} />
                      ))}
                    </div>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                      {form.password.length >= 12 ? '🟢 Strong' : form.password.length >= 8 ? '🔵 Good' : '🟡 Weak'}
                    </p>
                  </motion.div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={regLoading}
                  className="w-full py-4 bg-slate-900 text-white rounded-2xl flex items-center justify-center space-x-3 shadow-xl hover:bg-slate-800 hover:translate-y-[-2px] active:translate-y-0 transition-all disabled:opacity-50 mt-4"
                >
                  <AnimatePresence mode="wait">
                    {regLoading ? (
                      <motion.div key="spin" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    ) : (
                      <motion.div key="idle" className="flex items-center space-x-3 text-[11px] font-black uppercase tracking-widest">
                        <span>Create Account</span>
                        <FiArrowRight />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </button>

                <p className="text-center text-[9px] font-bold text-slate-300 uppercase tracking-widest pt-2">
                  Already have an account?{' '}
                  <button type="button" onClick={() => navigate('/admin/login')}
                    className="text-slate-500 hover:text-slate-900 transition-colors underline underline-offset-2">
                    Login
                  </button>
                </p>
              </form>
            </div>

            <p className="text-center mt-8 text-[9px] font-black text-slate-300 uppercase tracking-widest">
              Siyane Optometrists · Restricted Access
            </p>
          </motion.div>
        )}

      </AnimatePresence>
    </div>
  );
};

export default Register;
